import Vue from 'vue';
import resetState from '@/helpers/resetState';
import { getTransactionPromise } from '@/helpers/transaction';
import store from '@/store';
import { ABI } from './ABI';
import { formatData } from '@/constants/commonContent';
import { messages } from '@/constants/messages'
import { Contract } from 'web3-eth-contract';

const FEATURE_NAME = 'MemberFeature';

let from;

async function contract(): Promise<Contract> {
  from = {from: store.getters.getWallet};
  const address = store.getters.getFeatureAddress(FEATURE_NAME);
  return new Promise(resolve => {
    let contract = new window.web3.eth.Contract(ABI, address);
    resolve(contract);
  });
}

const initialState = {
  isApprovedMember: false,
  memberFee: 1000000000000000,
  memberCounter: null,
  members: {},
  isWaiting: false,
  waitingCounter: 0,
  waitings: {},
  memberAvatar: null,
  memberKey: null,
  memberData: { name: null, email: null }
};

const state = {
  isApprovedMember: initialState.isApprovedMember,
  memberFee: initialState.memberFee,
  memberCounter: initialState.memberCounter,
  members: initialState.members,
  isWaiting: initialState.isWaiting,
  waitingCounter: initialState.waitingCounter,
  waitings: initialState.waitings,
  memberAvatar: initialState.memberAvatar,
  memberKey: initialState.memberKey,
  memberData: initialState.memberData
};

const getters = {
  isApprovedMember: state => state.isApprovedMember,
  isMember: (state, getters) => (address?) => {
    let walletAddress = address;
    if (!address) walletAddress = getters.getWallet;
    if (state.members[walletAddress] === undefined) return false;
    if (state.members[walletAddress]) return true;
    return false;
  },
  getCurrentMember: (state, getters) => {
    if (getters.isReader) return true;
    let member = getters.getMember();
    return member ? member : null;
  },
  getMemberFee: state => state.memberFee,
  getMemberCounter: state => state.memberCounter,
  getMembers: state => state.members,
  getMember: (state, getters) => address => {
    let walletAddress = address;
    if (!address) walletAddress = getters.getWallet;
    return state.members[walletAddress];
  },
  getWaitingCounter: state => state.waitingCounter,
  getMemberAvatar: state => state.memberAvatar,
  getWaitings: state => state.waitings,
  getWaiting: state => address => state.waitings[address] || null,
  isWaiting: (state, getters) => address => {
    if (!address) return state.isWaiting;
    for (let addr in getters.getWaitings) {
      if (addr === address) return true;
    }
    return false;
  },
  getMemberKey: state => state.memberKey
};

const mutations = {
  setIsApprovedMember: (state, item) => {
    state.isApprovedMember = item;
  },
  setIsWaiting: (state, item) => {
    state.isWaiting = item;
  },
  setMemberCounter: (state, item) => {
    state.memberCounter = item;
  },
  setMember: (state, item) => {
    Vue.set(state.members, item.address, item);
  },
  setWaitingCounter: (state, item) => {
    state.waitingCounter = item;
  },
  setWaiting: (state, item) => {
    Vue.set(state.waitings, item.address, item);
  },
  resetWaitings: state => {
    state.waitings = {};
  },
  resetMemberFeature: state => {
    resetState(state, initialState);
  },
  setMemberAvatar: (state, item) => {
    state.memberAvatar = item;
  },
  setMemberKey: (state, item) => {
    state.memberKey = item;
  }
};

const actions = {
  initMemberFeature({ dispatch, commit, getters }) {
    dispatch('getMemberKeyDapp').then(() => dispatch('getOtherAssetsExt'));
    dispatch('isMemberDapp');
    dispatch('isWaitingDapp');
    dispatch('getMembersDapp');
    dispatch('getWaitingsDapp');

    //Events
    contract().then(contract => {
      let transactionHash = '0x';
      contract.events.MemberEvent().subscribe((err, res) => {
        if (!err && transactionHash !== res.transactionHash) {
          transactionHash = res.transactionHash;
          dispatch('getMembersDapp').then(() => {
            dispatch('getWaitingsDapp');
          });
          if (res.returnValues.addr === getters.getWallet) {
            dispatch('getMemberAvatarDapp', getters.getWallet);
            commit('updateMemberPending', false);
          }
        }
        commit('resetMailPending', 'joinToTheCompany');
        store.commit('approveMemberPending', null);
      });
      contract.events.WaitingEvent().subscribe((err, res) => {
        if (!err && transactionHash !== res.transactionHash) {
          transactionHash = res.transactionHash;
          console.log(res);
          dispatch('isWaitingDapp');
          dispatch('getWaitingsDapp');
        }
        commit('resetMailPending', 'joinToTheCompany');
      });
    });
  },
  /**
   * Get member key
   * @return {Promise<string>}
   */
  getMemberKeyDapp({ commit }) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getMemberKey()
          .call()
          .then(res => {
            commit('setMemberKey', res);
            resolve(res);
          })
          .catch(err =>  console.log(err, 'getMemberKeyDapp'));
      });
    });
  },
  /**
   * Check user role in company
   * @param "wallet address" {string}
   */
  isMemberDapp({ getters, commit }) {
    contract().then(contract => {
      contract.methods.isMember(getters.getWallet)
        .call()
        .then(res => {
          commit('setIsApprovedMember', res);
        })
        .catch(console.error);
    });
  },
  /**
   * Check is user in waiting
   * @param "wallet address" {string}
   */
  isWaitingDapp({ getters, commit }) {
    contract().then(contract => {
      contract.methods.isWaiting(getters.getWallet)
        .call()
        .then(res => {
          commit('setIsWaiting', res);
        })
        .catch(console.error);
    });
  },
  /**
   * Add first owner for initialization company
   * @param ownerName {string}
   * @param ownerEmail {string}
   * @return {Promise<void>}
   */
  addFirstOwnerDapp({}, data) {
    return new Promise((resolve, reject) => {
      contract().then(contract => {
        contract.methods.addFirstOwner(data.ownerName, data.ownerEmail)
          .send(from, (err, res) =>
            getTransactionPromise(resolve, reject, err, res)
          );
      });
    });
  },
  /**
   * Add treasury wallet
   * @param wallet {string}
   * @param name {string}
   * @param email {string}
   * @param value {number}
   */
  initTreasuryWalletDapp({ getters, commit }, data) {
    let name = 'Treasury wallet';
    let email = getters.getMember().email;
    contract().then(contract => {
      contract.methods.initTreasuryWallet(data.wallet, name, email, data.value)
        .send(from, (err) => {
          if (err) {
            commit('companyTreasuryWalletPending', false);
            Vue.toasted.error(messages.canceled);
          }
        });
    });
  },
  /**
   * Add first owner for company
   * @param ownerAddress {string}
   * @param ownerName {string}
   * @param ownerEmail {string}
   */
  addOwnerDapp({}, data) {
    contract().then(contract => {
      contract.methods.addOwner(data.ownerAddress, data.ownerName, data.ownerEmail)
        .send(from, (err) => {
          if (err) console.error('Error!');
        });
    });
  },
  addOwnersDapp({ dispatch }, data) {
    contract().then(() => {
      for (let i = 0; i < data.length; i++) {
        dispatch('addOwnerDapp', data[i]);
      }
    });
  },
  /**
   * Request from new user for join to the company
   * @param name {string}
   * @param email {string}
   * @param avatarName {string}
   * @param avatar {string}
   * @return {Promise<void>}
   */
  requestMembershipDapp({ commit, dispatch, getters }, data) {
    return new Promise((resolve, reject) => {
      contract().then(contract => {
        contract.methods.requestMembership(
          data.name,
          data.email,
          data.avatarName,
          data.avatar
        )
        .send(from, (err, res) => {
          if (err) {
            commit('updateMemberPending', false);
            Vue.toasted.error(messages.canceled);
          } else {
            dispatch('sendNewMemberMailExt', {
              name: data.name,
              email: data.email,
              time: Vue.moment().format(formatData.dateTime),
              tx: res
            });
            return getTransactionPromise(resolve, reject, err, res);
          }
        });
      });
    });
  },
  /**
   * Approve the request to join the organization of a new member
   * @param id {number}
   * @return {Promise<void>}
   */
  approveMembershipDapp({dispatch}, data) {
    return new Promise((resolve, reject) => {
      contract().then(contract => {
        contract.methods.approveMembership(data.id)
          .send(from, (err, res) => {
            if (!err) {
              dispatch('sendApproveNewMemberMailExt', {
                name: data.name,
                email: data.email,
                address: data.address,
                time: Vue.moment().format(formatData.dateTime),
                tx: res
              });
            }
            getTransactionPromise(resolve, reject, err, res);
          });
      });
    });
  },
  /**
   * Update member
   * @param avatarName {string}
   * @param avatar {string}
   * @param name {string}
   * @param email {string}
   * @param position {string}
   */
  updateMemberDapp({ commit }, data) {
    contract().then(contract => {
      contract.methods.updateMember(
        data.avatarName,
        data.avatar,
        data.name,
        data.email,
        data.position
      )
      .send(from, err => {
        if (err) {
          commit('updateMemberPending', false);
          Vue.toasted.error(messages.canceled);
        }
      });
    });
  },
  /**
   * Get member
   * @param id {name}
   * @res[0] address {string}
   * @res[1] name {string}
   * @res[2] email {string}
   * @res[3] position {string}
   * @res[4] time {number}
   * @res[5] approver {number}
   */
  getMemberDapp({ dispatch, getters, commit }, id) {
    contract().then(contract => {
      contract.methods.getMember(id)
        .call()
        .then(res => {
          let data = {
            id,
            address: res[0],
            name: res[1],
            email: res[2],
            position: res[3],
            creationTime: parseInt(res[4]),
            approver: res[5]
          };
          commit('setMember', data);
          dispatch('getBalanceDapp', data.address);
          if (data.address === getters.getWallet && !getters.getMemberAvatar) {
            dispatch('getMemberAvatarDapp', data.address);
          }
        })
        .catch(console.error);
    });
  },
  getMembersDapp({ dispatch }) {
    dispatch('getMemberCounterDapp').then(result => {
      for (let i = 0; i < result; i++) {
        dispatch('getMemberDapp', i);
      }
    });
  },
  /**
   * Get member avatar
   * @param address {string}
   * @res[0] "picture name" {string}
   * @res[1] "picture data" {string}
   */
  getMemberAvatarDapp({ getters, commit }, addr) {
    contract().then(contract => {
      contract.methods.getMemberPic(addr)
        .call()
        .then(hash => {
          if (!hash) return;
          getters.ipfs.get(hash[1]).then(res => {
            commit('setMemberAvatar', res);
          });
        })
        .catch(console.error);
    });
  },
  /**
   * Get count of member in company
   * @return {Promise<number>}
   * @res "count" {number}
   */
  getMemberCounterDapp({ commit }) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getMemberCounter()
          .call()
          .then(res => {
            commit('setMemberCounter', res);
            resolve(res);
          })
          .catch(console.error);
      });
    });
  },
  /**
   * Get a member who's waiting for approval
   * @param id {number}
   * @res[0] "member address" {string}
   * @res[1] "member name" {string}
   * @res[2] "member email" {string}
   */
  getWaitingDapp({ commit }, id) {
    contract().then(contract => {
      contract.methods.getWaiting(id)
        .call()
        .then(res => {
          const waiting = {
            id,
            address: res[0],
            name: res[1],
            email: res[2]
          };
          commit('setWaiting', waiting);
        })
        .catch(console.error);
    });
  },
  getWaitingsDapp({ dispatch, commit }) {
    commit('resetWaitings');
    dispatch('getWaitingCounterDapp').then(result => {
      for (var i = 0; i < result; i++) {
        dispatch('getWaitingDapp', i);
      }
    });
  },
  /**
   * Get count of members who's waiting for approval
   * @return {Promise<number>}
   * @res "count of members" {number}
   */
  getWaitingCounterDapp({ commit }) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getWaitingCounter()
          .call()
          .then(res => {
            commit('setWaitingCounter', res);
            resolve(res);
          })
          .catch(console.error);
      });
    });
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
